import { generateShortId } from '@o2d/shared';
import { checkboxSizesForScale } from '../../core/viewport/zoom-cluster.service.js';
import type { CheckboxObject } from './checkbox.interface.js';
import { DEFAULT_CHECKBOX_ICON, type CheckboxIcon } from './checkbox-icons.config.js';

const DEFAULT_COLOR = '#374151';
const DEFAULT_CHECKED = false;
const DEFAULT_LABEL = '';

/**
 * Freeze the payload in DEV builds so accidental mutations surface as a
 * TypeError instead of silently corrupting the CheckboxObject contract.
 */
function freezeInDev<T extends object>(value: T): T {
  if (import.meta.env?.DEV) {
    return Object.freeze(value);
  }
  return value;
}

/**
 * Stefan, 2026-09-03 — "choose properties before drawing": optional overrides
 * for the pre-placement-staged options-circle values.
 *
 * Review-Lauf 01 (2026-09-03): a plain merge onto this function's already-
 * frozen result used to live in `checkbox-placement.controller.ts` instead,
 * on the (incorrect) assumption that `createCheckboxDefaults` — assigned
 * directly as `SlicePlugin<CheckboxObject>.createDefaults`, whose fixed
 * signature is `(scale, point, sizeOpt: Size | null)` — had no room for an
 * options parameter. It does: the other six factories (rectangle, ellipse,
 * person, sticky, smiley, textbox) prove a trailing optional 4th parameter
 * is perfectly assignable. The controller-side merge also silently defeated
 * `freezeInDev()` for every checkbox placement (a fresh, unfrozen object
 * literal every time) — moving the merge back into the factory fixes both.
 */
export interface CreateCheckboxDefaultsOptions {
  readonly color?: string;
  readonly icon?: CheckboxIcon;
  /**
   * Abstract S/M/L key rather than a pre-computed world size — resolved
   * against `viewportScale` right here, mirroring the post-creation
   * `checkbox-size` click handler's own live-scale resolution (re-zooming
   * between staging and drawing uses the NEW zoom's size, not a stale one).
   */
  readonly sizeKey?: 'S' | 'M' | 'L';
}

/**
 * Build a fresh checkbox default payload — extracted from the inline
 * `placeCheckboxAt` body in `o2d-whiteboard.element.ts` per PRD-F2 §T6.
 *
 * Checkbox is Click-to-Place: the 3rd parameter (`sizeOpt`) exists ONLY to
 * satisfy `SlicePlugin<CheckboxObject>.createDefaults`'s fixed signature —
 * checkbox itself has no drag-to-size gesture and ignores it. The checkbox
 * is centred on the pointer at the `sizeKey`-selected (default "M") size for
 * the current viewport scale (Stefan, 2026-08-28 — was a fixed 40 WU
 * regardless of zoom before; see `checkboxSizesForScale`'s own docstring for
 * how M is derived and why it's the same value as
 * `creationDefaults().textFontSize`).
 */
export function createCheckboxDefaults(
  viewportScale: number,
  point: { x: number; y: number },
  _sizeOpt?: { width: number; height: number } | null,
  options?: CreateCheckboxDefaultsOptions,
): CheckboxObject {
  const sizeKey = options?.sizeKey ?? 'M';
  const size = checkboxSizesForScale(viewportScale)[sizeKey];
  const checkbox: CheckboxObject = {
    id: generateShortId(),
    type: 'checkbox',
    x: point.x - size / 2,
    y: point.y - size / 2,
    width: size,
    height: size,
    rotation: 0,
    checked: DEFAULT_CHECKED,
    color: options?.color ?? DEFAULT_COLOR,
    label: DEFAULT_LABEL,
    icon: options?.icon ?? DEFAULT_CHECKBOX_ICON,
  };
  return freezeInDev(checkbox);
}
